export function generateTimeSeries(
  points = 12,
  base = 100,
  variance = 50,
): { categories: string[]; values: number[] } {
  const categories: string[] = [];
  const values: number[] = [];
  const now = new Date();
  for (let i = points - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    categories.push(d.toLocaleString('default', { month: 'short' }));
    values.push(Math.round(base + Math.random() * variance));
  }
  return { categories, values };
}

export function generatePieData(): Array<{ name: string; value: number }> {
  return [
    { name: 'Direct', value: 1024 },
    { name: 'Organic', value: 735 },
    { name: 'Referral', value: 580 },
    { name: 'Social', value: 484 },
    { name: 'Email', value: 300 },
  ];
}

export function generateTableRows(count = 15): Record<string, unknown>[] {
  const statuses = ['Active', 'Pending', 'Closed'];
  const owners = ['Alice', 'Bob', 'Carol', 'Dan', 'Eve'];
  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    name: `Record #${i + 1}`,
    owner: owners[i % owners.length],
    status: statuses[i % statuses.length],
    amount: Math.round(Math.random() * 10_000),
    createdAt: new Date(Date.now() - i * 86_400_000).toISOString(),
  }));
}

export function generateActivity(
  count = 8,
): Array<{ id: string; text: string; time: string; user: string }> {
  const acts = ['created', 'updated', 'deleted', 'shared', 'exported'];
  const objects = ['a dashboard', 'a report', 'a widget', 'a user', 'a role'];
  const users = ['Alice', 'Bob', 'Carol', 'Dan'];
  return Array.from({ length: count }, (_, i) => ({
    id: `act_${i}`,
    user: users[i % users.length],
    text: `${acts[i % acts.length]} ${objects[i % objects.length]}`,
    time: new Date(Date.now() - i * 3_600_000).toISOString(),
  }));
}

export function generateScatterData(): Array<[number, number]> {
  return Array.from({ length: 40 }, () => [Math.random() * 100, Math.random() * 100]);
}

export function generateBubbleData(): { data: Array<{ x: number; y: number; size: number; name?: string }> } {
  return {
    data: Array.from({ length: 20 }, (_, i) => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 100 + 10,
      name: `Point ${i + 1}`,
    })),
  };
}

export function generateRadarData(): { indicators: Array<{ name: string; max: number }>; series: Array<{ name: string; data: number[] }> } {
  return {
    indicators: [
      { name: 'Sales', max: 100 },
      { name: 'Marketing', max: 100 },
      { name: 'R&D', max: 100 },
      { name: 'Support', max: 100 },
      { name: 'HR', max: 100 },
    ],
    series: [
      { name: 'Current Year', data: [85, 72, 60, 90, 55] },
      { name: 'Previous Year', data: [70, 65, 80, 75, 60] },
    ],
  };
}

export function generateBoxplotData(): { categories: string[]; data: number[][] } {
  return {
    categories: ['Q1', 'Q2', 'Q3', 'Q4'],
    data: [
      [650, 780, 920, 1050, 1200],
      [580, 700, 850, 980, 1150],
      [700, 820, 950, 1080, 1250],
      [620, 750, 880, 1020, 1180],
    ],
  };
}

export function generateHeatmapData(): { xData: string[]; yData: string[]; data: Array<[number, number, number]> } {
  const hours = ['12a', '1a', '2a', '3a', '4a', '5a', '6a', '7a', '8a', '9a', '10a', '11a', '12p', '1p', '2p', '3p', '4p', '5p', '6p', '7p', '8p', '9p', '10p', '11p'];
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const data: Array<[number, number, number]> = [];
  for (let i = 0; i < days.length; i++) {
    for (let j = 0; j < hours.length; j++) {
      data.push([j, i, Math.round(Math.random() * 100)]);
    }
  }
  return { xData: hours, yData: days, data };
}

export function generateCandlestickData(): { categories: string[]; data: Array<[number, number, number, number]> } {
  const now = new Date();
  const categories: string[] = [];
  const data: Array<[number, number, number, number]> = [];
  let base = 100;
  for (let i = 30; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 86_400_000);
    categories.push(`${d.getMonth() + 1}/${d.getDate()}`);
    const open = base + (Math.random() - 0.5) * 10;
    const close = open + (Math.random() - 0.5) * 15;
    const low = Math.min(open, close) - Math.random() * 5;
    const high = Math.max(open, close) + Math.random() * 5;
    data.push([open, close, low, high]);
    base = close;
  }
  return { categories, data };
}

export function generateSankeyData(): { nodes: Array<{ name: string }>; links: Array<{ source: string; target: string; value: number }> } {
  return {
    nodes: [
      { name: 'Page Views' },
      { name: 'Sign-ups' },
      { name: 'Trials' },
      { name: 'Paid' },
      { name: 'Churned' },
    ],
    links: [
      { source: 'Page Views', target: 'Sign-ups', value: 4200 },
      { source: 'Sign-ups', target: 'Trials', value: 1200 },
      { source: 'Trials', target: 'Paid', value: 600 },
      { source: 'Paid', target: 'Churned', value: 80 },
    ],
  };
}

export function generateFunnelData(): Array<{ name: string; value: number }> {
  return [
    { name: 'Visitors', value: 5000 },
    { name: 'Leads', value: 2500 },
    { name: 'Opportunities', value: 1200 },
    { name: 'Proposals', value: 600 },
    { name: 'Closed Won', value: 200 },
  ];
}

export function generateTreeData(): Array<{ name: string; children: Array<{ name: string; children?: Array<{ name: string; value?: number }> }> }> {
  return [{
    name: 'Root',
    children: [
      { name: 'North America', children: [{ name: 'USA', value: 350 }, { name: 'Canada', value: 120 }] },
      { name: 'Europe', children: [{ name: 'UK', value: 200 }, { name: 'Germany', value: 180 }, { name: 'France', value: 150 }] },
      { name: 'Asia', children: [{ name: 'Japan', value: 280 }, { name: 'China', value: 420 }] },
    ],
  }];
}

export function generateTreemapData(): Array<{ name: string; children: Array<{ name: string; value: number }> }> {
  return [{
    name: 'Revenue',
    children: [
      { name: 'Product A', value: 12000 },
      { name: 'Product B', value: 8000 },
      { name: 'Product C', value: 6000 },
      { name: 'Services', value: 4000 },
    ],
  }];
}

export function generateSunburstData(): Array<{ name: string; children: Array<{ name: string; value?: number; children?: Array<{ name: string; value: number }> }> }> {
  return [{
    name: 'All',
    children: [
      { name: 'Direct', children: [{ name: 'Online', value: 3500 }, { name: 'Store', value: 2500 }] },
      { name: 'Indirect', children: [{ name: 'Partner', value: 1800 }, { name: 'Reseller', value: 1200 }] },
    ],
  }];
}

export function generateGraphData(): { nodes: Array<{ name: string; category: number; symbolSize: number }>; links: Array<{ source: string; target: string; value: number }>; categories: Array<{ name: string }> } {
  return {
    nodes: [
      { name: 'Alice', category: 0, symbolSize: 30 },
      { name: 'Bob', category: 0, symbolSize: 25 },
      { name: 'Carol', category: 1, symbolSize: 20 },
      { name: 'Dan', category: 1, symbolSize: 20 },
      { name: 'Eve', category: 2, symbolSize: 15 },
      { name: 'Frank', category: 2, symbolSize: 15 },
      { name: 'Grace', category: 0, symbolSize: 25 },
    ],
    links: [
      { source: 'Alice', target: 'Bob', value: 5 },
      { source: 'Alice', target: 'Carol', value: 3 },
      { source: 'Bob', target: 'Dan', value: 4 },
      { source: 'Carol', target: 'Eve', value: 2 },
      { source: 'Dan', target: 'Frank', value: 3 },
      { source: 'Grace', target: 'Alice', value: 6 },
      { source: 'Grace', target: 'Eve', value: 1 },
    ],
    categories: [
      { name: 'Executive' },
      { name: 'Manager' },
      { name: 'Staff' },
    ],
  };
}

export function generateParallelData(): { dimensions: Array<{ name: string; min: number; max: number }>; data: number[][] } {
  return {
    dimensions: [
      { name: 'Income', min: 0, max: 200 },
      { name: 'Spend', min: 0, max: 100 },
      { name: 'Satisfaction', min: 0, max: 10 },
      { name: 'Loyalty', min: 0, max: 100 },
    ],
    data: Array.from({ length: 50 }, () => [
      Math.round(Math.random() * 200),
      Math.round(Math.random() * 100),
      Math.round(Math.random() * 10),
      Math.round(Math.random() * 100),
    ]),
  };
}

export function generateCalendarData(): Array<[string, number]> {
  const data: Array<[string, number]> = [];
  const start = new Date(2025, 0, 1);
  for (let i = 0; i < 365; i++) {
    const d = new Date(start.getTime() + i * 86_400_000);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    data.push([key, Math.round(Math.random() * 100)]);
  }
  return data;
}

export function generateThemeRiverData(): { data: Array<[string, number, string]>; topics: string[] } {
  const topics = ['Revenue', 'Costs', 'Profit'];
  const data: Array<[string, number, string]> = [];
  const start = new Date(2025, 0, 1);
  for (let i = 0; i < 12; i++) {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    const time = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    data.push([time, Math.round(Math.random() * 200 + 100), 'Revenue']);
    data.push([time, Math.round(Math.random() * 100 + 50), 'Costs']);
    data.push([time, Math.round(Math.random() * 100), 'Profit']);
  }
  return { data, topics };
}

export function generatePictorialBarData(): { categories: string[]; series: Array<{ name: string; data: number[] }> } {
  return {
    categories: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    series: [{ name: 'Value', data: [120, 200, 150, 80, 70] }],
  };
}

export function generateNightingaleData(): Array<{ name: string; value: number }> {
  return [
    { name: 'Segment A', value: 480 },
    { name: 'Segment B', value: 320 },
    { name: 'Segment C', value: 280 },
    { name: 'Segment D', value: 200 },
    { name: 'Segment E', value: 140 },
    { name: 'Segment F', value: 90 },
  ];
}

export function generateWaterfallData(): { categories: string[]; data: Array<{ name: string; value: number; isTotal?: boolean }> } {
  return {
    categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Q2 Total'],
    data: [
      { name: 'Jan', value: 100 },
      { name: 'Feb', value: 50 },
      { name: 'Mar', value: 80 },
      { name: 'Apr', value: -30 },
      { name: 'May', value: 70 },
      { name: 'Jun', value: 40 },
      { name: 'Q2 Total', value: 80, isTotal: true },
    ],
  };
}
